import React, { useEffect, useState } from 'react';
import { AppLayout } from '../../components/layout/AppLayout';
import { poolPredictionsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Trophy, TrendingUp, Clock, Users, DollarSign, Plus, CheckCircle2, XCircle } from 'lucide-react';
import { Label } from '../../components/ui/label';

export const PredictionsPage = () => {
  const [markets, setMarkets] = useState([]);
  const [myParticipations, setMyParticipations] = useState([]);
  const [myMultiBets, setMyMultiBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [entryFee, setEntryFee] = useState('');
  const [placing, setPlacing] = useState(false);
  
  // Accumulator state
  const [accumulatorMode, setAccumulatorMode] = useState(false);
  const [accumulatorSelections, setAccumulatorSelections] = useState([]);
  const [accumulatorStake, setAccumulatorStake] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [marketsRes, participationsRes, multiBetsRes] = await Promise.all([
        poolPredictionsAPI.listMarkets('open'),
        poolPredictionsAPI.getMyParticipations(),
        poolPredictionsAPI.getMyMultiBets()
      ]);
      setMarkets(marketsRes.data);
      setMyParticipations(participationsRes.data);
      setMyMultiBets(multiBetsRes.data);
    } catch (error) {
      toast.error('Failed to load predictions');
    } finally {
      setLoading(false);
    }
  };

  const openParticipateDialog = (market) => {
    setSelectedMarket(market);
    setSelectedOutcome(market.outcomes[0]?.name || '');
    setEntryFee(market.entry_fee?.toString() || '');
  };

  const handleParticipate = async () => {
    if (!selectedOutcome || !entryFee || parseFloat(entryFee) <= 0) {
      toast.error('Select outcome and enter amount');
      return;
    }
    
    setPlacing(true);
    try {
      await poolPredictionsAPI.participate(selectedMarket.id, {
        selected_outcome: selectedOutcome,
        entry_fee: parseFloat(entryFee)
      });
      toast.success(`Successfully joined pool for ${selectedOutcome}!`);
      setSelectedMarket(null);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to participate');
    } finally {
      setPlacing(false);
    }
  };

  const toggleAccumulatorSelection = (market, outcome) => {
    const existing = accumulatorSelections.find(s => s.market_id === market.id);
    if (existing) {
      setAccumulatorSelections(accumulatorSelections.filter(s => s.market_id !== market.id));
    } else {
      setAccumulatorSelections([...accumulatorSelections, { market_id: market.id, outcome, market_title: market.title }]);
    }
  };

  const handlePlaceAccumulator = async () => {
    if (accumulatorSelections.length < 2) {
      toast.error('Select at least 2 predictions for accumulator');
      return;
    }
    if (!accumulatorStake || parseFloat(accumulatorStake) <= 0) {
      toast.error('Enter stake amount');
      return;
    }

    setPlacing(true);
    try {
      await poolPredictionsAPI.createMultiBet({
        predictions: accumulatorSelections,
        total_stake: parseFloat(accumulatorStake)
      });
      toast.success(`Multi-bet placed with ${accumulatorSelections.length} selections!`);
      setAccumulatorMode(false);
      setAccumulatorSelections([]);
      setAccumulatorStake('');
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to place multi-bet');
    } finally {
      setPlacing(false);
    }
  };

  const formatTimeRemaining = (closesAt) => {
    if (!closesAt) return '';
    const now = new Date();
    const closeTime = new Date(closesAt);
    const diff = closeTime - now;
    
    if (diff < 0) return 'Closed';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'closed': return 'bg-yellow-500';
      case 'settled': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getParticipationStatus = (status) => {
    switch (status) {
      case 'active': return { color: 'bg-blue-500', text: 'Active' };
      case 'won': return { color: 'bg-green-500', text: 'Won' };
      case 'lost': return { color: 'bg-red-500', text: 'Lost' };
      case 'refunded': return { color: 'bg-gray-500', text: 'Refunded' };
      default: return { color: 'bg-gray-500', text: status };
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6 pb-20" data-testid="predictions-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Trophy className="h-8 w-8 text-yellow-500" /> Predictions
            </h1>
            <p className="mt-2 text-muted-foreground">Join pool-based prediction markets and win big</p>
          </div>
          <Button
            onClick={() => setAccumulatorMode(!accumulatorMode)}
            variant={accumulatorMode ? 'default' : 'outline'}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {accumulatorMode ? 'Cancel Accumulator' : 'Create Accumulator'}
          </Button>
        </div>

        {/* Accumulator Builder */}
        {accumulatorMode && (
          <Card className="border-blue-500 border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Accumulator Builder ({accumulatorSelections.length} selections)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {accumulatorSelections.length > 0 && (
                <div className="space-y-2">
                  {accumulatorSelections.map((sel, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{sel.market_title}</p>
                        <p className="text-sm text-muted-foreground">{sel.outcome}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setAccumulatorSelections(accumulatorSelections.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {accumulatorSelections.length >= 2 && (
                <div className="space-y-3 pt-4 border-t">
                  <div>
                    <Label>Total Stake</Label>
                    <Input
                      type="number"
                      placeholder="Enter stake amount"
                      value={accumulatorStake}
                      onChange={(e) => setAccumulatorStake(e.target.value)}
                    />
                  </div>
                  <Button
                    onClick={handlePlaceAccumulator}
                    disabled={placing}
                    className="w-full"
                  >
                    {placing ? 'Placing...' : `Place Accumulator Bet`}
                  </Button>
                </div>
              )}
              
              {accumulatorSelections.length < 2 && (
                <p className="text-center text-sm text-muted-foreground py-4">
                  Select at least 2 markets below to create an accumulator
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="markets" className="w-full">
          <TabsList>
            <TabsTrigger value="markets">Open Markets ({markets.length})</TabsTrigger>
            <TabsTrigger value="my-bets">My Predictions ({myParticipations.length})</TabsTrigger>
            <TabsTrigger value="multi-bets">Multi-Bets ({myMultiBets.length})</TabsTrigger>
          </TabsList>

          {/* Open Markets Tab */}
          <TabsContent value="markets" className="mt-6">
            {loading ? (
              <p className="text-center py-8">Loading markets...</p>
            ) : markets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center space-y-3">
                  <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">No open markets right now. Check back soon!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {markets.map((market) => (
                  <Card key={market.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{market.title}</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">{market.description}</p>
                        </div>
                        <Badge className={getStatusColor(market.status)}>{market.status}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatTimeRemaining(market.closes_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {market.participants_count || 0}
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          {market.current_pool?.toFixed(2) || '0.00'} {market.currency}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Outcomes:</p>
                        {market.outcomes?.map((outcome) => (
                          <div key={outcome.name} className="flex items-center justify-between p-2 bg-muted rounded">
                            <span className="text-sm">{outcome.name}</span>
                            {accumulatorMode ? (
                              <Button
                                size="sm"
                                variant={accumulatorSelections.find(s => s.market_id === market.id && s.outcome === outcome.name) ? 'default' : 'outline'}
                                onClick={() => toggleAccumulatorSelection(market, outcome.name)}
                              >
                                {accumulatorSelections.find(s => s.market_id === market.id && s.outcome === outcome.name) ? 'Selected' : 'Select'}
                              </Button>
                            ) : (
                              <span className="text-sm text-muted-foreground">~{outcome.estimated_odds?.toFixed(2)}x</span>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {!accumulatorMode && (
                        <Button
                          onClick={() => openParticipateDialog(market)}
                          className="w-full"
                          variant="default"
                        >
                          Join Pool - {market.entry_fee} {market.currency}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Predictions Tab */}
          <TabsContent value="my-bets" className="mt-6">
            {myParticipations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center space-y-3">
                  <Trophy className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">You haven't joined any predictions yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myParticipations.map((participation) => {
                  const statusInfo = getParticipationStatus(participation.status);
                  return (
                    <Card key={participation.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{participation.market_title}</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">{participation.market_category}</p>
                          </div>
                          <Badge className={statusInfo.color}>{statusInfo.text}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Your Selection</p>
                            <p className="font-medium">{participation.selected_outcome}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Entry Fee</p>
                            <p className="font-medium">{participation.entry_fee} {participation.currency}</p>
                          </div>
                          {participation.payout && (
                            <div className="col-span-2">
                              <p className="text-muted-foreground">Payout</p>
                              <p className="font-medium text-green-600">{participation.payout} {participation.currency}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Multi-Bets Tab */}
          <TabsContent value="multi-bets" className="mt-6">
            {myMultiBets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center space-y-3">
                  <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground">No multi-bet accumulators yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myMultiBets.map((multiBet) => (
                  <Card key={multiBet.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">Multi-Bet Accumulator</CardTitle>
                        <Badge className={getParticipationStatus(multiBet.status).color}>
                          {getParticipationStatus(multiBet.status).text}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Total Stake</p>
                          <p className="font-medium">{multiBet.total_stake} {multiBet.currency}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Accumulator Odds</p>
                          <p className="font-medium">{multiBet.accumulator_odds}x</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Potential Payout</p>
                          <p className="font-medium">{multiBet.potential_payout?.toFixed(2)} {multiBet.currency}</p>
                        </div>
                        {multiBet.actual_payout && (
                          <div>
                            <p className="text-muted-foreground">Actual Payout</p>
                            <p className="font-medium text-green-600">{multiBet.actual_payout} {multiBet.currency}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="border-t pt-4">
                        <p className="text-sm font-medium mb-2">Selections:</p>
                        <div className="space-y-2">
                          {multiBet.selections_enriched?.map((sel, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                              <div>
                                <p className="font-medium">{sel.market_title}</p>
                                <p className="text-muted-foreground">{sel.outcome}</p>
                              </div>
                              <Badge variant="outline">{sel.market_status}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Participate Dialog */}
      <Dialog open={!!selectedMarket} onOpenChange={() => setSelectedMarket(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Join Prediction Pool</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="font-medium">{selectedMarket?.title}</p>
              <p className="text-sm text-muted-foreground mt-1">{selectedMarket?.description}</p>
            </div>
            
            <div>
              <Label>Select Outcome</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={selectedOutcome}
                onChange={(e) => setSelectedOutcome(e.target.value)}
              >
                {selectedMarket?.outcomes?.map((outcome) => (
                  <option key={outcome.name} value={outcome.name}>
                    {outcome.name} (~{outcome.estimated_odds?.toFixed(2)}x)
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Entry Fee</Label>
              <Input
                type="number"
                value={entryFee}
                onChange={(e) => setEntryFee(e.target.value)}
                placeholder="Enter amount"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Min: {selectedMarket?.min_entry_fee || selectedMarket?.entry_fee} | Max: {selectedMarket?.max_entry_fee || selectedMarket?.entry_fee} {selectedMarket?.currency}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedMarket(null)}>Cancel</Button>
            <Button onClick={handleParticipate} disabled={placing}>
              {placing ? 'Joining...' : 'Join Pool'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};
